import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Joi from "joi-browser";
import geo from "../services/geoService";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { getAlert } from "../services/appAlerts";
import useForm from "../app/hooks/useForm";
import useControls from "../app/hooks/useControls";
import { getCodeManager, loadCodeManager } from "../app/store/referenceData";

const schema = {
  title: Joi.string().required().label("Title / Role"),
  name: Joi.string().required().label("Name"),
  email: Joi.string().email().required().label("Email"),
  contact: Joi.string()
    .length(10)
    .options({ language: { string: { length: "Contact Number must be 10 digits." } } })
    .options({})
    .regex(/^[0-9]+$/)
    .options({ language: { string: { regex: { base: "This entry can only contain numbers" } } } })
    .label("Contact Number"),
};

function AddWorkflowPerformer(props) {
  const {
    selectedObj,
    showHide,
    handleFormSubmit,
    handlePermissionShowHide,
    handleModalShowHide,
  } = props;

  const [stateList, setStateList] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedCountyId, setSelectedCountyId] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [loginType, setLoginType] = useState(0);
  const [azureEmailId, setAzureEmailId] = useState("");
  const dispatch = useDispatch();
  const allRoles = useSelector(getCodeManager("County User Title"));

  const doSubmit = async () => {
    const performer = stateData;
    const perf = { ...performer };
    if (loginType === 1) {
      const azureEmailRegex = /^[a-zA-Z0-9]+$/;
      if (!azureEmailId || !azureEmailRegex.test(azureEmailId)) {
        getAlert("", "Azure Email ID must contain only letters and numbers.", "error");
        return;
      }
      perf["azureEmailId"] = `${azureEmailId}@workfinity1.onmicrosoft.us`;
    } else {
      perf["azureEmailId"] = "";
    }
    perf["loginType"] = loginType;
    handleFormSubmit(perf);
    if (performer["id"] > 0) {
      getAlert("", "User Details Updated Successfully", "success");
    } else {
      getAlert("", "User Details Added Successfully", "success");
    }
    closeForm();
  };

  const {
    validate,
    handleChange,
    stateData,
    setStateData,
    stateErrors,
    setStateErrors,
    handleSubmit,
  } = useForm(schema, doSubmit);

  const { renderInput } = useControls(stateData, stateErrors, handleChange, schema);

  useEffect(() => {
    let isMounted = true;
    async function loadInitialData() {
      const stateList = await geo.getStateList();
      dispatch(loadCodeManager("County User Title"));
      if (isMounted) {
        setStateList(stateList);
      }
    }
    loadInitialData();
    return () => { isMounted = false; };
  }, [dispatch]);

  const updateState = useCallback((name, value) => {
    setStateData((d) => ({ ...d, [name]: value }));
    setStateErrors((e) => { const n = { ...e }; delete n[name]; return n; });
  }, [setStateData, setStateErrors]);

  const bindCountyDropdown = useCallback(async (geonameId) => {
    return geonameId > 0 ? await geo.getGeoData(geonameId) : [];
  }, []);

  const closeForm = useCallback(() => {
    if (handlePermissionShowHide) handlePermissionShowHide();
    else if (handleModalShowHide) handleModalShowHide();
  }, [handleModalShowHide, handlePermissionShowHide]);

  const handleStateChanged = async ({ target: input }) => {
    const selectedOption = input.selectedOptions[0];
    setSelectedStateId(selectedOption.value);
    updateState(input.name, selectedOption.text);
    await bindCountyDropdown(selectedOption.value);
  };

  const handleChange1 = (selectedOption) => {
    let interfaces = [];
    if (selectedOption)
      selectedOption.forEach((item) => interfaces.push({ id: item.value, interfaceName: item.label }));
    setStateData((d) => ({ ...d, accessInterfaces: interfaces }));
    setSelectedOption(selectedOption);
  };

  useEffect(() => {
    let isMounted = true;
    async function syncSelectedObject() {
      let availableStates = stateList;
      if (!stateList || stateList.length === 0) {
        availableStates = await geo.getStateList();
        if (isMounted) setStateList(availableStates);
      }
      if (selectedObj) {
        setStateData(selectedObj);
        setLoginType(selectedObj.loginType || 0);
        let azure = selectedObj?.azureEmailId || "";
        if (azure.endsWith("@workfinity1.onmicrosoft.us"))
          azure = azure.replace("@workfinity1.onmicrosoft.us", "");
        setAzureEmailId(azure);
        const selectedState = availableStates.filter((x) => x.name === selectedObj.state);
        if (selectedState.length > 0) {
          setSelectedStateId(selectedState[0].geonameId);
          const countyList = await bindCountyDropdown(selectedState[0].geonameId);
          if (selectedObj.county !== null) {
            const selectedCounty = countyList.filter((x) => x.name === selectedObj.county);
            if (selectedCounty.length > 0) setSelectedCountyId(selectedCounty[0].geonameId);
          }
        } else {
          setSelectedStateId("");
          setSelectedCountyId("");
        }
        if (selectedObj.accessInterfaces && selectedObj.accessInterfaces.length > 0) {
          const options = [];
          selectedObj.accessInterfaces.forEach((item) =>
            options.push({ value: item.id, label: item.interfaceName })
          );
          setSelectedOption(options);
        } else {
          setSelectedOption([]);
        }
      } else {
        setStateData({});
        setSelectedStateId("");
        setSelectedCountyId("");
        setSelectedOption([]);
        setLoginType(0);
        setAzureEmailId("");
      }
    }
    syncSelectedObject();
    return () => { isMounted = false; };
  }, [bindCountyDropdown, selectedObj, setStateData, stateList]);

  const formatPhone = (value) => {
    if (!value) return "";
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const rawVal = e.target.value.replace(/[^\d]/g, "");
    handleChange({
      currentTarget: {
        name: e.target.name,
        value: rawVal,
      },
    });
  };

  const isEditMode = Boolean(stateData && stateData.id > 0);
  const isModal = showHide !== undefined;

  const externalAppOptions = [
    { value: "1", label: "DDRMS" },
    { value: "2", label: "BPRM" },
  ];

  const formContent = (
    <div className="awp-card">
      <style>{`
        /* ── Shared wf- overrides scoped inside .awp-card ── */
        @keyframes awpFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .awp-card {
          border: 1px solid rgba(226,232,240,0.8);
          border-radius: 0;
          background: #ffffff;
          box-shadow: none;
          margin-top: 10px;
          animation: awpFadeIn 0.3s ease both;
          overflow: hidden;
        }
        .awp-modal-body .awp-card {
          border: none !important; box-shadow: none !important;
          padding: 0 !important; margin-top: 0 !important;
          background: transparent !important; animation: none !important;
        }

        /* ── Form Header (light grey-white banner) ── */
        .awp-form-header {
          display: flex; align-items: center; gap: 14px;
          padding: 16px 22px;
          background: #ffffff;
          border-bottom: 1.5px solid #e2e8f0;
          position: relative; overflow: hidden;
        }
        .awp-form-header-icon {
          width: 42px; height: 42px; border-radius: 0;
          background: #f5f5f5; border: 1.5px solid #d4d4d4;
          display: flex; align-items: center; justify-content: center;
          font-size: 17px; color: #525252; flex-shrink: 0; position: relative; z-index: 1;
        }
        .awp-form-header-text { position: relative; z-index: 1; }
        .awp-form-header-title {
          color: #0f172a; font-size: 14.5px; font-weight: 600; margin: 0; line-height: 1.2;
        }
        .awp-form-header-sub {
          color: #64748b; font-size: 12px; margin-top: 3px; font-weight: 600;
        }
        .awp-form-header-badge {
          margin-left: auto; position: relative; z-index: 1;
          background: transparent !important; border: 1px solid #e2e8f0;
          border-radius: 0; padding: 4px 12px;
          color: #475569; font-size: 11.5px; font-weight: 600;
          display: flex; align-items: center; gap: 6px;
        }

        /* ── Form Body ── */
        .awp-form-body { padding: 18px 22px; }

        /* ── Section Dividers ── */
        .awp-section { margin-bottom: 22px; }
        .awp-section-header {
          display: flex; align-items: center; gap: 8px;
          margin-bottom: 14px; padding-bottom: 9px;
          border-bottom: 1.5px solid #f1f5f9;
        }
        .awp-section-icon {
          width: 26px; height: 26px; border-radius: 0;
          background: #f5f5f5; border: 1px solid #d4d4d4;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; color: #525252;
        }
        .awp-section-title {
          font-size: 11px; font-weight: 600; color: #475569;
          text-transform: uppercase; letter-spacing: 0.7px;
        }

        /* ── wf-style inputs (matching WorkflowUser) ── */
        .awp-card .wf-control-label {
          font-size: 11px !important; font-weight: 600 !important; color: #64748b !important;
          text-transform: uppercase !important; letter-spacing: 0.7px !important;
          margin-bottom: 6px !important; display: flex !important; align-items: center !important; gap: 5px !important;
        }
        .awp-card .wf-control-label i { color: #94a3b8; font-size: 11px; }
        .awp-card .form-control-label i,
        .awp-card .wf-control-label i,
        .awp-card .wf-input-icon { display: none !important; }

        /* Required asterisk override to prevent absolute positioning overlap */
        .awp-card .required .form-control-label::after,
        .awp-card .required .wf-control-label::after {
          content: " *" !important;
          position: static !important;
          font-family: inherit !important;
          color: #ef4444 !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          line-height: 1 !important;
          margin-left: 3px !important;
        }
        /* Also the explicit awp-req marker */
        .awp-req {
          color: #ef4444;
          font-weight: 600;
          font-size: 13px;
          margin-left: 2px;
          line-height: 1;
        }

        /* Override stock form-control-label to match wf style */
        .awp-card .form-control-label {
          font-size: 11px !important; font-weight: 600 !important; color: #64748b !important;
          text-transform: uppercase !important; letter-spacing: 0.7px !important;
          margin-bottom: 6px !important; display: flex !important;
          align-items: center !important; gap: 5px !important;
        }
        .awp-card .form-group { margin-bottom: 16px; }

        /* ── wf-input-wrap / icon layout ── */
        .awp-card .wf-input-wrap { position: relative; }
        .awp-card .wf-input-icon {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          color: #94a3b8; font-size: 13px; pointer-events: none;
          transition: color 0.2s ease; z-index: 1;
        }
        .awp-card .wf-input-wrap:focus-within .wf-input-icon { color: #2563eb; }

        /* form-control styled as wf-input */
        .awp-card .form-control,
        .awp-card .awp-wf-select {
          border-radius: 0 !important;
          border: 1.5px solid #e2e8f0 !important;
          padding: 9px 14px !important;
          font-size: 13.5px !important;
          color: #1e293b !important;
          background: #ffffff !important;
          transition: all 0.2s cubic-bezier(0.4,0,0.2,1) !important;
          box-shadow: 0 1px 3px 0 rgba(0,0,0,0.03) !important;
          width: 100% !important;
        }
        .awp-card .form-control::placeholder { color: #94a3b8 !important; }
        .awp-card .form-control:hover,
        .awp-card .awp-wf-select:hover {
          border-color: #94a3b8 !important;
          box-shadow: 0 2px 6px -1px rgba(0,0,0,0.06) !important;
        }
        .awp-card .form-control:focus,
        .awp-card .awp-wf-select:focus {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.1), 0 2px 6px -1px rgba(37,99,235,0.08) !important;
          outline: none !important; background: #fafbff !important;
        }
        /* Selects have no text padding-left; handled by awp-wf-select without left icon */
        .awp-card .awp-wf-select-noicon {
          padding-left: 13px !important;
          appearance: none !important; -webkit-appearance: none !important;
          background: #ffffff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E") no-repeat right 12px center !important;
          padding-right: 32px !important;
          cursor: pointer !important;
        }
        /* Selects with a left icon */
        .awp-card .awp-wf-select-withicon {
          padding-left: 14px !important;
          appearance: none !important; -webkit-appearance: none !important;
          background: #ffffff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E") no-repeat right 12px center !important;
          padding-right: 32px !important;
          cursor: pointer !important;
        }
        /* Alert override — compact */
        .awp-card .alert.alert-danger {
          padding: 5px 10px !important; font-size: 11.5px !important;
          border-radius: 0 !important; margin-top: 5px !important; margin-bottom: 0 !important;
        }

        /* react-select match */
        .awp-card div[class*="-control"] {
          border: 1.5px solid #e2e8f0 !important; border-radius: 0 !important;
          min-height: 38px !important; box-shadow: none !important;
          transition: all 0.2s ease !important; background: #ffffff !important;
        }
        .awp-card div[class*="-control"]:hover { border-color: #94a3b8 !important; }
        .awp-card div[class*="-control"]:focus-within {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.1) !important;
        }
        .awp-card div[class*="-ValueContainer"] { padding: 0 8px !important; }
        .awp-card div[class*="-placeholder"] { color: #94a3b8 !important; font-size: 13px !important; }
        .awp-card div[class*="-singleValue"],
        .awp-card div[class*="-multiValue"] { font-size: 13px !important; }

        /* ── Auth Cards ── */
        .awp-auth-card {
          flex: 1; border: 2px solid #e2e8f0; border-radius: 0;
          padding: 12px 14px; display: flex; align-items: center; gap: 11px;
          cursor: pointer; transition: all 0.2s ease; position: relative;
          background: #ffffff; user-select: none;
        }
        .awp-auth-card:hover {
          border-color: #cbd5e1; background: #f8fafc;
          transform: translateY(-1px); box-shadow: 0 4px 12px rgba(15,23,42,0.03);
        }
        .awp-auth-card.active {
          border-color: #171717; background: #ffffff;
          box-shadow: none;
        }
        .awp-auth-card-icon {
          font-size: 18px; min-width: 36px; height: 36px; border-radius: 0;
          display: flex; align-items: center; justify-content: center;
          background: #f1f5f9; transition: all 0.2s ease; color: #64748b;
        }
        .awp-auth-card.active .awp-auth-card-icon {
          background: #171717; color: #ffffff !important;
        }
        .awp-auth-card-content { display: flex; flex-direction: column; text-align: left; }
        .awp-auth-card-title { font-size: 13px; font-weight: 600; color: #1e293b; }
        .awp-auth-card-desc { font-size: 11px; color: #64748b; margin-top: 2px; line-height: 1.3; }
        .awp-auth-card-badge {
          position: absolute; top: 10px; right: 10px;
          color: #171717; font-size: 15px; line-height: 1;
        }

        /* ── Azure field ── */
        .awp-azure-field {
          border: 1.5px solid #e2e8f0; border-radius: 0;
          background: #ffffff; padding: 13px 15px;
          animation: awpFadeIn 0.25s ease both;
        }
        .awp-card .input-group .form-control {
          border-radius: 0 !important;
          padding-left: 14px !important;
        }
        .awp-card .input-group-text {
          border: 1.5px solid #e2e8f0 !important; border-left: none !important;
          border-radius: 0 !important; background: #ffffff !important;
          color: #475569 !important; font-size: 12.5px !important;
          padding: 0 14px !important; display: flex; align-items: center;
        }

        /* ── Field hint ── */
        .awp-field-hint {
          font-size: 11px; color: #94a3b8; margin-top: 4px;
          display: flex; align-items: center; gap: 4px;
        }

        /* ── Form Footer ── */
        .awp-form-footer {
          display: flex; align-items: center; justify-content: center;
          gap: 10px; flex-wrap: wrap; padding: 14px 22px;
          border-top: 1.5px solid #f1f5f9; background: #ffffff;
        }
      `}</style>

      {/* ── Form Header ── */}
      <div className="awp-form-header">
        <div className="awp-form-header-icon">
          <i className={`fa ${isEditMode ? "fa-pencil-square-o" : "fa-user-plus"}`} />
        </div>
        <div className="awp-form-header-text">
          <p className="awp-form-header-title">
            {isEditMode ? "Edit User Details" : "Add New Workflow User"}
          </p>
          <p className="awp-form-header-sub">
            {isEditMode
              ? `Updating record for ${stateData.name || "selected user"}`
              : "Fill in the details below to register a new user to this workflow role"}
          </p>
        </div>
        {isEditMode && stateData.id && (
          <div className="awp-form-header-badge">
            <i className="fa fa-id-badge" /> User #{stateData.id}
          </div>
        )}
      </div>

      {/* ── Form Body ── */}
      <div className="awp-form-body">
        <div className="awp-section">
          <div className="awp-section-header">
            <div className="awp-section-icon"><i className="fa fa-id-card-o" /></div>
            <span className="awp-section-title">Role &amp; Identity</span>
          </div>
          <div className="row">

            {/* Title / Role */}
            <div className="col-lg-6 form-group required">
              <label className="form-control-label" htmlFor="awpTitleSelect">
                <i className="fa fa-tag" />
                Title / Role
              </label>
              <div className="wf-input-wrap">
                <i className="fa fa-tag wf-input-icon" aria-hidden="true" />
                <select
                  id="awpTitleSelect"
                  className="form-control awp-wf-select awp-wf-select-withicon"
                  value={stateData.title || ""}
                  onChange={(e) => updateState("title", e.target.value)}
                >
                  <option value="">— Select a title / role —</option>
                  {allRoles.map((role) => (
                    <option key={role.id || role.name} value={role.name}>{role.name}</option>
                  ))}
                </select>
              </div>
              {stateErrors["title"] && (
                <div className="alert alert-danger">{stateErrors["title"]}</div>
              )}
              <p className="awp-field-hint">
                <i className="fa fa-info-circle" />
                Select the job title or role this user will hold within the workflow.
              </p>
            </div>

            {/* Full Name */}
            <div className="col-lg-6 form-group required">
              <label className="form-control-label" htmlFor="name">
                <i className="fa fa-user" /> Full Name
              </label>
              <div className="wf-input-wrap">
                <i className="fa fa-user wf-input-icon" aria-hidden="true" />
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={stateData.name || ""}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Full Name"
                />
              </div>
              {stateErrors.name && (
                <div className="alert alert-danger">{stateErrors.name}</div>
              )}
            </div>

            {/* Email */}
            <div className="col-lg-6 form-group required">
              <label className="form-control-label" htmlFor="email">
                <i className="fa fa-envelope" /> Email Address
              </label>
              <div className="wf-input-wrap">
                <i className="fa fa-envelope wf-input-icon" aria-hidden="true" />
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={stateData.email || ""}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Email Address"
                />
              </div>
              {stateErrors.email && (
                <div className="alert alert-danger">{stateErrors.email}</div>
              )}
            </div>

            {/* Mobile */}
            <div className="col-lg-6 form-group">
              <label className="form-control-label" htmlFor="contact">
                <i className="fa fa-phone" /> Mobile No.
              </label>
              <div className="wf-input-wrap">
                <i className="fa fa-phone wf-input-icon" aria-hidden="true" />
                <input
                  type="text"
                  name="contact"
                  id="contact"
                  value={formatPhone(stateData.contact || "")}
                  onChange={handlePhoneChange}
                  className="form-control"
                  placeholder="Mobile No."
                />
              </div>
              {stateErrors.contact && (
                <div className="alert alert-danger">{stateErrors.contact}</div>
              )}
            </div>

          </div>
        </div>

        {/* Section 2: Location */}
        <div className="awp-section">
          <div className="awp-section-header">
            <div className="awp-section-icon"><i className="fa fa-map-marker" /></div>
            <span className="awp-section-title">Location</span>
          </div>
          <div className="row">

            {/* State */}
            <div className="col-lg-6 form-group">
              <label className="form-control-label" htmlFor="awpStateList">
                <i className="fa fa-globe" />
                State
              </label>
              <div className="wf-input-wrap">
                <i className="fa fa-globe wf-input-icon" aria-hidden="true" />
                <select
                  className="form-control awp-wf-select awp-wf-select-withicon"
                  id="awpStateList"
                  name="state"
                  value={selectedStateId}
                  onChange={handleStateChanged}
                >
                  <option value="0">— Select State —</option>
                  {stateList.map((s) => (
                    <option key={s.geonameId} value={s.geonameId}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* City */}
            <div className="col-lg-6 form-group">
              <label className="form-control-label" htmlFor="county">
                <i className="fa fa-building" /> City
              </label>
              <div className="wf-input-wrap">
                <i className="fa fa-building wf-input-icon" aria-hidden="true" />
                <input
                  type="text"
                  name="county"
                  id="county"
                  value={stateData.county || ""}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="City"
                />
              </div>
              {stateErrors.county && (
                <div className="alert alert-danger">{stateErrors.county}</div>
              )}
            </div>

          </div>
        </div>

        {/* Section 3: Authentication */}
        <div className="awp-section">
          <div className="awp-section-header">
            <div className="awp-section-icon"><i className="fa fa-lock" /></div>
            <span className="awp-section-title">Login Authentication</span>
          </div>
          <div className="row">
            <div className="col-lg-12 form-group">
              <div className="d-flex align-items-stretch" style={{ gap: "14px", flexWrap: "wrap" }}>
                <div
                  className={`awp-auth-card${loginType === 0 ? " active" : ""}`}
                  onClick={() => setLoginType(0)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && setLoginType(0)}
                >
                  <div className="awp-auth-card-icon"><i className="fa fa-desktop" /></div>
                  <div className="awp-auth-card-content">
                    <span className="awp-auth-card-title">System Login</span>
                    <span className="awp-auth-card-desc">Standard local credentials access</span>
                  </div>
                  {loginType === 0 && (
                    <div className="awp-auth-card-badge"><i className="fa fa-check-circle" /></div>
                  )}
                </div>

                <div
                  className={`awp-auth-card${loginType === 1 ? " active" : ""}`}
                  onClick={() => setLoginType(1)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && setLoginType(1)}
                >
                  <div className="awp-auth-card-icon"><i className="fa fa-windows" /></div>
                  <div className="awp-auth-card-content">
                    <span className="awp-auth-card-title">Azure AD Login</span>
                    <span className="awp-auth-card-desc">Microsoft cloud single sign-on</span>
                  </div>
                  {loginType === 1 && (
                    <div className="awp-auth-card-badge"><i className="fa fa-check-circle" /></div>
                  )}
                </div>
              </div>
            </div>

            {loginType === 1 && (
              <div className="col-lg-12 form-group">
                <div className="awp-azure-field">
                  <label className="form-control-label" htmlFor="awpAzureEmailId">
                    <i className="fa fa-windows" style={{ color: "#2563eb" }} />
                    Azure AD Email ID
                  </label>
                  <div className="input-group">
                    <div className="wf-input-wrap" style={{ flex: 1 }}>
                      <i className="fa fa-at wf-input-icon" aria-hidden="true" />
                      <input
                        type="text"
                        id="awpAzureEmailId"
                        className="form-control"
                        value={azureEmailId}
                        onChange={(e) => setAzureEmailId(e.target.value)}
                        placeholder="e.g. john.doe"
                        autoComplete="off"
                        autoCapitalize="none"
                      />
                    </div>
                    <div className="input-group-append">
                      <span className="input-group-text" style={{ fontWeight: 600 }}>
                        @workfinity1.onmicrosoft.us
                      </span>
                    </div>
                  </div>
                  <p className="awp-field-hint">
                    <i className="fa fa-info-circle" />
                    Enter only the username part — the domain is appended automatically.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 4: External Applications */}
        <div className="awp-section" style={{ marginBottom: 0 }}>
          <div className="awp-section-header">
            <div className="awp-section-icon"><i className="fa fa-plug" /></div>
            <span className="awp-section-title">External Application Access</span>
          </div>
          <div className="row">
            <div className="col-lg-12 form-group" style={{ marginBottom: 0 }}>
              <label className="form-control-label" htmlFor="awpAccessToExternal">
                <i className="fa fa-plug" />
                Access to External Applications
              </label>
              <Select
                inputId="awpAccessToExternal"
                value={selectedOption}
                onChange={handleChange1}
                options={externalAppOptions}
                isMulti={true}
                placeholder="Select external application(s)…"
                noOptionsMessage={() => "No applications available"}
              />
              <p className="awp-field-hint">
                <i className="fa fa-info-circle" />
                Optional — grant access to one or more external systems.
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* ── Form Footer ── */}
      <div className="awp-form-footer">
        <button
          type="button"
          className="btn-action-search"
          onClick={closeForm}
        >
          <i className="fa fa-arrow-left" aria-hidden="true" />
          {isModal ? "Cancel" : "Back to Users"}
        </button>
        <button
          className="btn-action-search btn-primary-search"
          disabled={!!validate()}
          onClick={handleSubmit}
        >
          <i className="fa fa-check" aria-hidden="true" />
          {isEditMode ? "Save Changes" : "Add User"}
        </button>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <Modal show={showHide} onHide={handleModalShowHide} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 text-primary" style={{ fontWeight: 600 }}>
            <i className={`fa ${isEditMode ? "fa-pencil-square-o" : "fa-user-plus"} mr-2`} />
            {isEditMode ? "Edit User Details" : "Add New User"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="awp-modal-body pt-2 pb-4">
          {formContent}
        </Modal.Body>
      </Modal>
    );
  }

  return formContent;
}

export default AddWorkflowPerformer;
